import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EmptyInfoCont,
  FirstPlaceHead,
  NoDataText,
  ScoreHeader,
  NameCont,
  ResTile,
  ResultsCont,
  ScoreCont,
  StatisticsBoxCont,
  StatHeaderCont,
  ScoreValue,
} from './StatisticsBox.styles';
import { EStatisticType } from '../../../constans/report';
import { RouteNames } from '../../../constans/routes';
import Tooltip from '../../../HOC/Tooltip/Tooltip';
import { useFilters } from '../../../hooks/useFilters';
import { useScroll } from '../../../hooks/useScroll';
import {
  ILeadersPerformersResults,
  IStrengthsAndWeaknessesTopic,
  ITeamPerformersResults,
  ITopicResAr,
} from '../../../services/reports';
import { BodyRegularS } from '../../../styles/globals';
import ScoreTrend from '../../ScoreTrend/ScoreTrend';
import InfoIcon from '@/assets/icons/infogrey.svg?react';

interface HandleClickParams {
  category?: string;
  code?: string;
  reportId?: string;
}

interface HandleClickOptions {
  isScrollToTopic?: boolean;
  isReportLink?: boolean;
}

interface IStatisticsBox {
  name: string;
  description: string;
  results:
    | ITopicResAr[]
    | IStrengthsAndWeaknessesTopic[]
    | ITeamPerformersResults[]
    | ILeadersPerformersResults[];
  statisticType?: EStatisticType;
  noDataText?: string;
  columnName?: string;
}

const StatisticsBox: FC<IStatisticsBox> = ({
  name,
  description,
  results,
  noDataText,
  statisticType = 'topBottomTopics',
  columnName,
}) => {
  const { scrollToId } = useScroll();
  const { applyAndMergeFilters, applyNestedFilter } = useFilters();
  const { t } = useTranslation();
  const reportKey = 'surveyReport';

  const goToSelectedTopic = async (topicID: string, categoryID: string) => {
    await scrollToId(categoryID);
    if (typeof applyAndMergeFilters === 'function') {
      await applyAndMergeFilters(reportKey, categoryID, { active: true, [topicID]: { active: true } });
    } else {
      await applyNestedFilter({ active: 'true' }, categoryID, reportKey);
    }
    await scrollToId(topicID);
  };

  const goToTeamReport = (id: string) => {
    window.open(`${RouteNames.Reports}/${id}/`, '_blank', 'noopener,noreferrer');
  };

  const handleTileClick = (
    { category, code, reportId }: HandleClickParams,
    { isScrollToTopic, isReportLink }: HandleClickOptions
  ) => {
    if (code && category && isScrollToTopic) {
      goToSelectedTopic(code, category);

      return;
    }

    if (reportId && isReportLink) {
      goToTeamReport(reportId);

      return;
    }
  };

  const getElemInfo = useCallback(
    (
      data:
        | ITopicResAr
        | IStrengthsAndWeaknessesTopic
        | ITeamPerformersResults
        | ILeadersPerformersResults
    ) => {
      switch (statisticType) {
        case 'topBottomTopics': {
          return {
            gradeStatus: (data as ITopicResAr)?.rating?.grade,
            score: (data as ITopicResAr).result.score,
            category: (data as ITopicResAr).topic.category,
            code: (data as ITopicResAr).topic.code,
            title: (data as ITopicResAr).topic.title,
            benchmarks: (data as ITopicResAr).benchmarks,
            isScrollToTopic: true,
          };
        }
        case 'topBottomTeams': {
          return {
            gradeStatus: (data as ITeamPerformersResults)?.result.rating,
            score: (data as ITeamPerformersResults).result.score,
            code: (data as ITeamPerformersResults).team.id,
            title: (data as ITeamPerformersResults).team.name,
            benchmarks: (data as ITeamPerformersResults).benchmarks,
            reportId: (data as ITeamPerformersResults).team.reportId,
            isReportLink: true,
          };
        }
        case 'topBottomLeaders': {
          return {
            gradeStatus: (data as ILeadersPerformersResults)?.result.rating,
            score: (data as ILeadersPerformersResults).result.score,
            title: (data as ILeadersPerformersResults).leader,
            benchmarks: (data as ILeadersPerformersResults).benchmarks,
          };
        }
        case 'strengthWeaknesses': {
          return {
            score: (data as IStrengthsAndWeaknessesTopic).result.count,
            percentage: (data as IStrengthsAndWeaknessesTopic).result.share,
            category: (data as ITopicResAr).topic.category,
            code: (data as IStrengthsAndWeaknessesTopic).topic.code,
            title: (data as IStrengthsAndWeaknessesTopic).topic.title,
            gradeStatus: (data as IStrengthsAndWeaknessesTopic).result.rating,
            benchmarks: (data as IStrengthsAndWeaknessesTopic).benchmarks,
            isScrollToTopic: true,
          };
        }
        default: {
          return {
            gradeStatus: (data as ITopicResAr)?.rating?.grade,
            score: (data as ITopicResAr).result.score,
            code: (data as ITopicResAr).topic.code,
            title: (data as ITopicResAr).topic.title,
            benchmarks: (data as ITopicResAr).benchmarks,
          };
        }
      }
    },
    [statisticType]
  );

  const renderStatTile = useMemo(() => {
    return results.map(
      (
        topic:
          | ITopicResAr
          | IStrengthsAndWeaknessesTopic
          | ITeamPerformersResults
          | ILeadersPerformersResults,
        index
      ) => {
        let {
          percentage,
          score,
          gradeStatus,
          title,
          category,
          code,
          reportId,
          isReportLink,
          isScrollToTopic,
          benchmarks,
        } = getElemInfo(topic);

        const { historical } = benchmarks ?? {};

        return (
          <ResTile
            data-testid="statisticsScrollButton"
            onClick={() =>
              handleTileClick({ category, code, reportId }, { isReportLink, isScrollToTopic })
            }
            status={gradeStatus}
            key={code || index}
            isClickAvailable={isReportLink || isScrollToTopic}
          >
            <NameCont id="NameCont">
              <BodyRegularS id="BodyRegularS">{title}</BodyRegularS>
            </NameCont>
            <ScoreCont id="ScoreCont">
              <ScoreValue id="ScoreValue">{score}</ScoreValue>
              {historical ? (
                <ScoreTrend
                  progress={historical?.change}
                  stateType={historical?.changeDirectionality}
                  isBorderless
                />
              ) : null}
            </ScoreCont>
            {percentage && (
              <ScoreCont id="ScoreCont">
                <ScoreValue id="ScoreValue">{percentage}%</ScoreValue>
              </ScoreCont>
            )}
          </ResTile>
        );
      }
    );
  }, [getElemInfo, goToSelectedTopic, results]);

  return (
    <StatisticsBoxCont id="StatisticsBoxCont">
      <StatHeaderCont id="StatHeaderCont">
        <FirstPlaceHead id="FirstPlaceHead">
          {name}
          {description && (
            <Tooltip positionX="3px" positionY="18px" tooltipDesc={description}>
              <InfoIcon data-testid="InfoIconOnStatistics" />
            </Tooltip>
          )}
        </FirstPlaceHead>
        <ScoreHeader id="ScoreHeader">{columnName || t('report.topicsStats.score')}</ScoreHeader>
        {statisticType === 'strengthWeaknesses' && (
          <ScoreHeader id="ScoreHeader">{t('report.topicsStats.percentage')}</ScoreHeader>
        )}
      </StatHeaderCont>
      {results && results.length > 0 ? (
        <ResultsCont id="ResultsCont">{renderStatTile}</ResultsCont>
      ) : (
        <EmptyInfoCont id="EmptyInfoCont">
          <NoDataText id="CaptionBoldLUppercase">
            {noDataText || t('report.topicsStats.noData')}
          </NoDataText>
        </EmptyInfoCont>
      )}
    </StatisticsBoxCont>
  );
};

export default StatisticsBox;
